import { GradientCircleStats } from "./gradient_circle_stats";

interface GradientCircleData {
	value: string;
	title: string;
	description: string;
}

interface EvidenceSectionProps {
	source?: React.ReactNode;
	heading?: string;
	description?: string;
	circles: [GradientCircleData, GradientCircleData, GradientCircleData];
}

export function EvidenceSection({ 
	source, 
	heading = "Learning intelligence that actually delivers", 
	description = "When universities implement StudyStash, they see measurable improvements across key metrics that drive institutional success:",
	circles 
}: EvidenceSectionProps) {
	const colors = ["bg-ss-light-green", "bg-ss-yellow", "bg-ss-purple"];

	const render_circle = (index: number, horizontal_layout = false) => (
		<GradientCircleStats
			key={`circle-${index}-${horizontal_layout ? 'mobile' : 'desktop'}`}
			value={circles[index].value}
			title={circles[index].title}
			description={circles[index].description}
			color={colors[index]}
			showArrow={true}
			horizontalLayout={horizontal_layout}
		/>
	);

	return (
		<div className="container mx-auto px-4 sm:px-6 lg:px-8">
			<section className="relative py-12 sm:py-16 md:py-20 lg:py-32 bg-linear-to-l from-ss-light-green via-ss-green to-ss-blue rounded-2xl sm:rounded-3xl overflow-hidden">
				{source && (
					<div className="absolute top-3 sm:top-4 md:top-6 right-3 sm:right-4 md:right-8">
						<h6 className="text-white text-[9px] sm:text-[10px] md:text-xs italic font-medium">{source}</h6>
					</div>
				)}
				
				{/* Header */}
				<div className="text-center mb-8 sm:mb-12 md:mb-16">
					<h2 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-3 sm:mb-4 md:mb-6 leading-tight text-white px-2">
						{heading}
					</h2>
					<p className="text-sm sm:text-base lg:text-lg xl:text-xl mb-4 sm:mb-6 md:mb-8 leading-relaxed opacity-90 text-white max-w-[95%] sm:max-w-2xl md:max-w-3xl mx-auto px-2">
						{description}
					</p>
				</div>

				{/* Stats Grid */}
				<div className="relative max-w-7xl mx-auto">
					{/* Desktop Grid Layout */}
					<div className="hidden lg:grid lg:grid-cols-3 gap-12 px-5">
						{/* SVG Connection Lines */}
						<div className="absolute inset-x-[15%] top-[20%]">
							<div className="absolute left-[15%] right-[55%]">
								<img src="/loops/small-line.svg" alt="Illustration of the StudyStash teaching-learning loop" className="w-full scale-[1.2]" />
							</div>
							<div className="absolute left-[55%] right-[15%]">
								<img src="/loops/small-line-2.svg" alt="Illustration of the StudyStash teaching-learning loop" className="w-full scale-[1.2]" />
							</div>
						</div>

						{circles.map((_, index) => render_circle(index))}
					</div>

					{/* Mobile/Tablet Layout */}
					<div className="flex flex-col mt-16 sm:mt-0 gap-16 sm:gap-10 md:gap-8 lg:hidden max-w-[80%] mx-auto">
						{circles.map((_, index) => (
							<div key={`mobile-${index}`} className="relative">
								{render_circle(index, true)}
							</div>
						))}
					</div>
				</div>
			</section>
		</div>
	);
}
